// Meilisearch-driven faceted search, opted into per-section via
// main-search.liquid's "Use Meilisearch filtering instead" setting.
// Products only - Meilisearch's index has no blog articles or pages,
// so unlike native search those simply won't appear here (accepted
// trade-off, confirmed with the store owner).
//
// Structurally a near-twin of llm-meilisearch-collection.js (same
// element ids/classes, so llm-meilisearch-collection.css applies
// unchanged) - the real differences are: no `collections = "..."`
// filter clause (searches the whole catalog), the query text comes
// from this page's own `?q=` param instead of a fixed collection
// handle, "Featured" has no collection to key a position off of
// (falls back to available:desc + Meilisearch's own text-relevance
// ranking), and card HTML is fetched via a plain /products/{handle}
// URL rather than a /collections/{handle}/products/{handle} one.

(function () {
  /* Answer formatting lives in ONE place: assets/llm-answer-format.js, loaded ahead of this file
     and shared with llm-collection-helper.js. Do not paste a copy back in here.

     A duplicate used to sit at this spot, with a comment claiming the shared file "404s on the CDN".
     It does not. That was measured against /cdn/shop/t/35/, a PREVIEW theme my browser was pinned
     to by a preview cookie; live is t/34, where the file has always returned 200. Nothing was ever
     wrong with the deploy — see [[reference_shopify_asset_serving_traps]]. */

  const root = document.getElementById('llm-meilisearch-collection');
  const configEl = document.getElementById('llm-meilisearch-config');
  if (!root || !configEl) return;

  const config = JSON.parse(configEl.textContent);
  if (!config.host || !config.searchKey) {
    console.error('[llm-meilisearch] Missing host/searchKey - set them under Theme settings > Meilisearch filtering.');
    root.innerHTML = '<p>Search is not configured yet.</p>';
    return;
  }

  const searchTerm = new URLSearchParams(window.location.search).get('q') || '';

  const filtersEl = document.getElementById('llm-meilisearch-filters-content');
  const gridEl = document.getElementById('llm-meilisearch-grid');
  const countEl = document.getElementById('llm-meilisearch-count');
  const sortEl = document.getElementById('llm-meilisearch-sort-native');
  const paginationEl = document.getElementById('llm-meilisearch-pagination');

  const PER_PAGE = config.perPage || 16;
  const MAX_VISIBLE_OPTIONS = config.maxFilterOptions || 7;

  /* Which matchingStrategy the CURRENT result set was produced with.
     runSearch() decides this per query (see the note there) and the accurate
     facet-count helper reads it back, because a count fetched under a different
     strategy than the grid disagrees with the grid. Never hard-code the strategy
     in two places again. */
  let effectiveStrategy = 'frequency';

  // Fields present in the index that aren't meaningful as checkbox
  // facets - always excluded, regardless of merchant config.
  const STRUCTURAL_EXCLUDED_FIELDS = new Set(['collections', 'price_min', 'price_max']);

  // Merchant-editable via the "Facet Field Config" metaobject (Content >
  // Metaobjects in Shopify admin, auto-seeded by the sync worker with one
  // row per real field it finds in the catalog). Keyed by the field name
  // as it actually appears in Meilisearch (metafields use
  // `metafield__<key>`, not the config's raw "metafields.ns.key"
  // identifier - same transform the sync worker uses, see
  // metafieldIndexKey in shopify.js).
  function meilisearchAttributeName(fieldIdentifier) {
    if (!fieldIdentifier.startsWith('metafields.')) return fieldIdentifier;
    const indexKey = fieldIdentifier.replace(/^metafields\./, '').replace(/[^a-zA-Z0-9]/g, '_');
    return `metafield__${indexKey}`;
  }

  // Facet Field Config / Facet Value Mapping are fetched from the sync
  // worker directly (not inlined via Liquid's `shop.metaobjects.<type>.
  // values`, which silently truncates at 50 entries - a real problem
  // once this catalog's messy size values pushed Facet Value Mapping
  // past 1,000 rows). See loadFacetConfig below.
  let FIELD_CONFIG = {};
  let VALUE_LABEL_LOOKUP = {};
  let LABEL_RANK_LOOKUP = {};

  const SYNC_WORKER_URL = 'https://llmedico-meilisearch-sync.llmedico.workers.dev';
  let facetConfigPromise = null;
  function ensureFacetConfigLoaded() {
    if (!facetConfigPromise) facetConfigPromise = loadFacetConfig();
    return facetConfigPromise;
  }
  // Kick off immediately (not awaited here) so it loads in parallel with
  // the first Meilisearch search request instead of adding a sequential
  // round-trip - runSearch() awaits the same (already in-flight) promise.
  ensureFacetConfigLoaded();

  async function loadFacetConfig() {
    try {
      const res = await fetch(`${SYNC_WORKER_URL}/config/facets`);
      const data = await res.json();

      const fieldConfig = {};
      for (const e of data.facetFieldConfig || []) {
        if (!e.field) continue;
        fieldConfig[meilisearchAttributeName(e.field)] = {
          displayName: e.displayName,
          showAsFilter: e.showAsFilter === true || e.showAsFilter === 'true',
          showAsSort: e.showAsSort === true || e.showAsSort === 'true',
          fieldOrder: e.fieldOrder,
        };
      }
      FIELD_CONFIG = fieldConfig;

      const valueLookup = {};
      const rankLookup = {};
      for (const m of data.facetValueMappings || []) {
        if (!m.field || !m.raw || !m.label) continue;
        const attr = meilisearchAttributeName(m.field);
        if (!valueLookup[attr]) valueLookup[attr] = {};
        valueLookup[attr][m.raw] = m.label;
        if (m.sortRank != null) {
          if (!rankLookup[attr]) rankLookup[attr] = {};
          rankLookup[attr][m.label] = m.sortRank;
        }
      }
      VALUE_LABEL_LOOKUP = valueLookup;
      LABEL_RANK_LOOKUP = rankLookup;
    } catch (err) {
      console.error('[llm-meilisearch] failed to load facet config:', err);
    }
  }

  // A field only ever appears in Meilisearch's facetDistribution if the
  // sync already registered it as filterable (which itself only happens
  // when Show as Filter is on) - so this exclusion set only needs to
  // catch the always-registered structural fields, plus the rare case of
  // a field whose config row hasn't synced yet (fail closed: hide it).
  function isExcludedField(field) {
    if (STRUCTURAL_EXCLUDED_FIELDS.has(field)) return true;
    return FIELD_CONFIG[field] ? !FIELD_CONFIG[field].showAsFilter : false;
  }

  const AVAILABLE_VALUE_LABELS = { true: 'In stock', false: 'Out of stock' };

  function humanizeOptionSlug(slug) {
    return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function fieldLabel(field) {
    if (FIELD_CONFIG[field]?.displayName) return FIELD_CONFIG[field].displayName;
    if (field === 'available') return 'Availability';
    if (field.startsWith('options.')) return humanizeOptionSlug(field.slice('options.'.length));
    return humanizeOptionSlug(field);
  }

  function valueLabel(field, value) {
    if (field === 'available') return AVAILABLE_VALUE_LABELS[value] ?? String(value);
    return String(value);
  }

  // Collapses raw facet values sharing the same mapped (or default)
  // display label into one entry - e.g. "MD"/"Medium"/"M" all mapped to
  // "Medium" become a single checkbox whose count is the sum of all
  // three, and which filters by all three raw values when checked.
  function groupFacetValues(field, valueEntries) {
    const groups = new Map();
    for (const [rawValue, count] of valueEntries) {
      const label = (VALUE_LABEL_LOOKUP[field] && VALUE_LABEL_LOOKUP[field][rawValue]) || valueLabel(field, rawValue);
      if (!groups.has(label)) groups.set(label, { label, rawValues: [], count: 0 });
      const group = groups.get(label);
      group.rawValues.push(rawValue);
      group.count += count;
    }
    const rankFor = (label) => (LABEL_RANK_LOOKUP[field] && LABEL_RANK_LOOKUP[field][label]) ?? null;
    return [...groups.values()].sort((a, b) => {
      const rankA = rankFor(a.label);
      const rankB = rankFor(b.label);
      if (rankA != null && rankB != null) return rankA - rankB;
      if (rankA != null) return -1;
      if (rankB != null) return 1;
      return a.label.localeCompare(b.label);
    });
  }

  const activeFilters = new Map();
  let currentOffset = 0;
  let currentSort = 'featured';
  let latestRequestId = 0;

  function buildSortArray() {
    // Sold-out products always sink to the very bottom, in every sort
    // mode - availability is a strict partition applied before any
    // other criterion, not something that competes with rating/price.
    const sort = ['available:desc'];
    if (currentSort === 'rating:desc') {
      sort.push('rating:desc', 'rating_count:desc');
    } else if (currentSort && currentSort !== 'featured') {
      sort.push(currentSort);
    }
    /* ⚠️ THE PARTITION ABOVE IS ENFORCED BY AN INDEX SETTING, NOT BY THIS LINE.
       Meilisearch's default rankingRules put `sort` FOURTH:
         ["words","typo","proximity","sort","rating_count:desc","attribute","exactness"]
       so with a real query the text rules run first and `sort` only breaks ties INSIDE a relevance
       bucket. The comment at the top of this function claimed sold-out always sinks to the bottom;
       for three weeks it did not. Owner searched "red light" (2026-09-21) and the top three hits
       were all sold out — "Red Light Therapy Device" beat the in-stock resistance bands on
       `words`/`proximity` before `available:desc` was ever consulted.

       Fixed on the INDEX (zz_rankingrules_fix.mjs), promoting sort to first:
         ["sort","words","typo","proximity","rating_count:desc","attribute","exactness"]
       Collection pages are unaffected because they always query with q:'' (llm-meilisearch-
       collection.js) — with no query text, words/typo/proximity cannot discriminate, so sort was
       already their effective first rule. Verified after the change: a position-sorted collection
       query still returned positions 0..11 in exact curated order.

       ⛔ If sold-out products creep back up the results, check the index's rankingRules BEFORE
       touching this function. Re-ordering this array cannot fix a ranking-rule problem. */
    return sort;
  }

  // Raw option values routinely contain literal `"` (inches, e.g. `2XL
  // (30" to 56" waist)`) - unescaped, that breaks out of the filter
  // string's own quoting and produces an invalid Meilisearch filter
  // expression (confirmed: caused every filter click to fail with
  // "Something went wrong loading results").
  function escapeFilterValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function buildFilterExpression() {
    const clauses = [];
    for (const [field, values] of activeFilters) {
      if (values.size === 0) continue;
      const ors = [...values].map((v) => `${field} = "${escapeFilterValue(v)}"`).join(' OR ');
      clauses.push(values.size > 1 ? `(${ors})` : ors);
    }
    return clauses.join(' AND ');
  }

  // True de-duplicated product count for a merged facet group (see the
  // over-counting note where this is called from renderFacets). Reuses
  // every OTHER currently active filter (any existing selection on this
  // SAME field is deliberately dropped, matching how Meilisearch's own
  // facetDistribution always ignores a field's own filter when counting
  // that field's options) plus one extra clause for this group's raw
  // values, and reads back the real hit count instead of a sum.
  async function fetchAccurateGroupCount(field, rawValues) {
    const clauses = [];
    for (const [f, values] of activeFilters) {
      if (f === field || values.size === 0) continue;
      const ors = [...values].map((v) => `${f} = "${escapeFilterValue(v)}"`).join(' OR ');
      clauses.push(values.size > 1 ? `(${ors})` : ors);
    }
    const groupOrs = rawValues.map((v) => `${field} = "${escapeFilterValue(v)}"`).join(' OR ');
    clauses.push(rawValues.length > 1 ? `(${groupOrs})` : groupOrs);

    try {
      const res = await fetch(`${config.host}/indexes/${config.index}/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.searchKey}`,
        },
        body: JSON.stringify({
          q: searchTerm,
          filter: clauses.join(' AND '),
          limit: 0,
          matchingStrategy: effectiveStrategy, // must match the main query or the counts disagree
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.estimatedTotalHits ?? null;
    } catch (err) {
      console.error('[llm-meilisearch] accurate facet count failed:', err);
      return null;
    }
  }

  async function runSearch() {
    await ensureFacetConfigLoaded();
    const requestId = ++latestRequestId;
    gridEl.setAttribute('aria-busy', 'true');

    const body = {
      q: searchTerm,
      filter: buildFilterExpression(),
      facets: ['*'],
      limit: PER_PAGE,
      offset: currentOffset,
      sort: buildSortArray(),
    };

    /* MATCHING STRATEGY — "all" first, "frequency" only as a rescue.
     *
     * The two strategies fail in opposite directions and we need both:
     *
     *   frequency  drops query words until SOMETHING matches. It exists because the
     *              default ("last") never drops the FIRST word, so one unknown leading
     *              word ("nonrinse shampoo and body wash") returned ZERO while the rest
     *              of the phrase matched 47 products.
     *   all        requires every word. Precise, but returns 0 the moment one word is
     *              unknown — which is the failure frequency was brought in to fix.
     *
     * Running frequency unconditionally made specific two-word searches noisy: measured
     * 2026-09-17, "eye patch" returned 21 products — it dropped "eye" and searched
     * "patch", so Salonpas/Biofreeze/lidocaine pain patches filled the tail behind the
     * 6 real eye patches. Under "all" the same query returns exactly those 6.
     *
     * So: ask for precision, and only widen when precision finds nothing. A query that
     * has real full-phrase matches shows only those; a query with an unknown word still
     * gets rescued exactly as before. Costs one extra request ONLY on the zero path.
     *
     * ⚠️ Whatever we land on is recorded in effectiveStrategy, because the accurate
     * facet-count helper must fetch its counts under the same strategy as the grid.
     *
     * ⛔ EXCEPT FOR IDENTIFIERS — NEVER WIDEN A PART NUMBER.
     * Owner, 2026-10-20: searching "M59988-10" returned 1,155 results. Measured: 'all' matched 0,
     * 'frequency' then dropped tokens until it hit "10" and returned 13% of the catalogue, led by
     * adult diapers. We do not stock M59988-10 — it is a McKesson hand pump mentioned in the body
     * copy of the perineal wash as "sold separately" — so the correct answer was no results.
     *
     * The rescue is right for PROSE ("nonrinse shampoo" -> drop the unknown word, keep the rest)
     * and worthless for a CODE: a part number is matched exactly or not at all, and half of one
     * means nothing. Someone typing a number off a packing slip is the most specific intent we
     * ever see, and the widened set is the least useful possible reply to it.
     *
     * ⚠️ A RESULT-COUNT CAP WOULD BE THE WRONG GUARD. It was the obvious one, and it would break
     * the rescue this file exists to provide: "nonrinse wipes" legitimately widens to ~300 wipes.
     * The distinction is the SHAPE OF THE QUERY, not the size of the answer. */
    const q = searchTerm.trim();
    // No whitespace + contains a digit = SKU, part number, UPC. "4x4" counts and should: if it
    // does not match exactly there is nothing useful to widen to. Prose always has a space, or
    // has no digits at all.
    const looksLikeCode = q !== '' && !/\s/.test(q) && /\d/.test(q);
    const strategies = !q ? ['frequency'] : (looksLikeCode ? ['all'] : ['all', 'frequency']);

    let data;
    try {
      for (const strategy of strategies) {
        const res = await fetch(`${config.host}/indexes/${config.index}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${config.searchKey}`,
          },
          body: JSON.stringify({ ...body, matchingStrategy: strategy }),
        });
        if (!res.ok) throw new Error(`Meilisearch search ${res.status}: ${await res.text()}`);
        data = await res.json();
        effectiveStrategy = strategy;
        // Stop as soon as a strategy matched anything. Deliberately estimatedTotalHits and
        // NOT hits.length: this request carries an offset, so on page 2+ of a short result
        // set hits.length is legitimately 0 while the query matched fine — keying off it
        // would silently widen page 2 to 'frequency' while page 1 stayed precise.
        if ((data.estimatedTotalHits ?? (data.hits || []).length) > 0) break;
      }
    } catch (err) {
      console.error('[llm-meilisearch] search failed:', err);
      gridEl.innerHTML = '<li class="col-span-full">Something went wrong loading results.</li>';
      return;
    }

    if (requestId !== latestRequestId) return;

    renderFacets(data.facetDistribution || {});
    renderCount(data.estimatedTotalHits ?? data.hits.length);
    renderPagination(data.estimatedTotalHits ?? data.hits.length);
    await renderGrid(data.hits);
  }

  /* ── PAGE matches (owner 2026-08-18) ─────────────────────────────────────────
     The Meilisearch index is PRODUCTS-ONLY, so any search for something that is a
     page rather than a product returned a flat "No results". The zero-result export
     showed that costing real traffic: "autoship" + "autosh" + "autoshi" +
     "autoship orders" = 32 searches for a feature we actively promote, plus
     "customer service phone number", "military", "is there an app".

     Pages come from Shopify's own /search/suggest.json (the same endpoint the
     instant-search dropdown already uses for collections) and render ABOVE the
     product grid. Deliberately rendered even when the product count is zero - that
     no-results case is exactly when the page IS the answer. Fails soft: any error
     and the page block is simply skipped, products render as before. */
  const PAGE_LIMIT = 3;

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  async function renderPageMatches() {
    if (!searchTerm) return;
    let pages = [];
    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(searchTerm)}` +
        `&resources[type]=page&resources[limit]=${PAGE_LIMIT}`
      );
      if (!res.ok) return;
      const json = await res.json();
      pages = (json && json.resources && json.resources.results && json.resources.results.pages) || [];
    } catch (err) {
      return; // fail soft - never let a page lookup break the product results
    }
    if (!pages.length) return;

    let box = document.getElementById('llm-search-pages');
    if (!box) {
      box = document.createElement('div');
      box.id = 'llm-search-pages';
      box.className = 'llm-search-pages';
      // above the product grid, below the result count
      gridEl.parentNode.insertBefore(box, gridEl);
    }

    box.innerHTML =
      `<h2 class="llm-search-pages__heading">Pages</h2><ul class="llm-search-pages__list">` +
      pages.map((p) => {
        // suggest.json appends tracking params (_pos/_psq/_ss); the clean path is nicer
        const href = String(p.url || '').split('?')[0];
        return `<li class="llm-search-pages__item">
            <a class="llm-search-pages__link" href="${escHtml(href)}">
              <span class="llm-search-pages__title">${escHtml(p.title)}</span>
              <span class="llm-search-pages__badge">Page</span>
            </a>
          </li>`;
      }).join('') +
      `</ul>`;
  }

  const pageStyle = document.createElement('style');
  pageStyle.textContent =
    '.llm-search-pages{margin:0 0 24px}' +
    '.llm-search-pages__heading{font-size:15px;font-weight:700;margin:0 0 8px;color:#122036}' +
    '.llm-search-pages__list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px}' +
    '.llm-search-pages__link{display:flex;align-items:center;gap:10px;padding:12px 14px;border:1px solid #e2e8ee;' +
    'border-radius:10px;background:#fff;text-decoration:none;transition:border-color .15s,box-shadow .15s}' +
    '.llm-search-pages__link:hover{border-color:#ff4438;box-shadow:0 1px 3px rgba(0,0,0,.06)}' +
    '.llm-search-pages__title{font-weight:600;color:#122036}' +
    '.llm-search-pages__badge{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;' +
    'color:#5b6b80;background:#f1f4f8;border-radius:999px;padding:3px 8px}';
  document.head.appendChild(pageStyle);

  /* Fired once, HERE rather than up beside ensureFacetConfigLoaded(): PAGE_LIMIT is a
     const declared just above, so calling this any earlier hits its temporal dead zone
     and throws a ReferenceError - which renderPageMatches()'s own catch swallowed,
     making the block silently never render. The search term is fixed for the life of
     the page (it comes from ?q=), so once is enough - re-running it on every facet
     toggle would be pure waste. Not awaited: pages appear when they arrive. */
  renderPageMatches();

  /* ZERO-RESULT HELP — a real conversation, not a single canned reply.
     A failed search was a dead end: two messages saying nothing was found and no way
     forward. The first version answered once and then offered no way to reply, which is
     arguably worse — it invites a question it cannot hear (owner 2026-08-20).
     So this keeps history and takes follow-ups, exactly like the collection helper. It is
     unscoped (no collection filter) because the whole point is to search everything we
     stock, and it only runs on zero results, so it costs nothing on successful searches. */
  let zeroHelpStarted = false;
  const zeroConvo = [];

  function hideZeroResultHelp() {
    const el = document.getElementById('llm-zero-help');
    if (el) el.hidden = true;
  }

  function zeroHelpBox() {
    let box = document.getElementById('llm-zero-help');
    if (box) return box;
    box = document.createElement('div');
    box.id = 'llm-zero-help';
    box.className = 'llm-cah llm-cah--active';
    box.innerHTML =
      '<div class="llm-cah__bar">' +
        '<span class="llm-cah__spark" aria-hidden="true">&#10022;</span>' +
        '<div class="llm-cah__intro">' +
          '<p class="llm-cah__title">Let us help you find it</p>' +
          '<p class="llm-cah__sub">Searching everything we stock.</p>' +
        '</div>' +
      '</div>' +
      '<div class="llm-cah__convo is-open"><div id="llm-zero-body"></div></div>' +
      '<form class="llm-cah__ask" autocomplete="off">' +
        '<label class="llm-cah__label" for="llm-zero-q">Ask a question</label>' +
        '<input id="llm-zero-q" class="llm-cah__input" type="text" maxlength="300" ' +
          'placeholder="Tell us what you need it for...">' +
        '<button type="submit" class="llm-cah__go">Ask</button>' +
      '</form>';
    /* ABOVE the grid. Placed after it, the answer sat below sixteen product cards — for
       "will my insurance take care of the cost?" the customer would scroll past 43 irrelevant
       products to reach the reply, if they found it at all (owner 2026-08-20). The count line
       stays on top, so the order reads: what we searched, the answer, then the products. */
    gridEl.parentNode.insertBefore(box, gridEl);

    box.querySelector('.llm-cah__ask').addEventListener('submit', function (e) {
      e.preventDefault();
      const input = box.querySelector('#llm-zero-q');
      const q = input.value.trim();
      if (!q) return;
      input.value = '';
      zeroTurn(q);
    });
    return box;
  }

  /* One exchange: show the question, stream the answer, link what it names. */
  async function zeroTurn(question, displayAs, collapsible) {
    const endpoint = window.LLM_CHAT_ENDPOINT;
    const box = zeroHelpBox();
    const body = box.querySelector('#llm-zero-body');
    const go = box.querySelector('.llm-cah__go');
    if (!endpoint) return;
    go.disabled = true;

    const said = document.createElement('p');
    said.className = 'llm-cah__said';
    /* The opening turn is phrased for the MODEL. Show the customer what they actually did,
       or the panel reads as though they typed instructions to it. */
    said.textContent = displayAs || question;
    body.appendChild(said);

    const status = document.createElement('p');
    status.className = 'llm-cah__status';
    status.textContent = 'Looking';
    body.appendChild(status);

    zeroConvo.push({ role: 'user', content: question });

    let res;
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: zeroConvo.slice(-12) }),
      });
    } catch (err) { res = null; }

    status.remove();
    go.disabled = false;

    if (!res || !res.ok) {
      const err = document.createElement('p');
      err.className = 'llm-cah__err';
      err.textContent = 'Sorry, we could not reach the assistant. Please try again, or contact our care team.';
      body.appendChild(err);
      zeroConvo.pop();
      return;
    }

    const reply = document.createElement('p');
    /* `is-clamped` is acted on only inside the mobile media query in llm-collection-helper.css,
       which this page already loads — so it is inert on desktop and caps the answer on a phone.

       ⚠️ THIS FILE IS A SECOND COPY OF THE HELPER UI. The collection-page helper
       (llm-collection-helper.js) renders the same `.llm-cah__*` markup from entirely separate code,
       and the clamp had to be implemented in BOTH — adding it to the collection helper alone left
       /search unchanged, which is exactly what the owner saw when he tested it (2026-09-13).
       Anything that changes how an answer is presented needs doing here too, or it silently only
       applies to half the site. */
    reply.className = 'llm-cah__reply';
    body.appendChild(reply);

    /* Collapse BEFORE the first chunk arrives, not after the stream ends.

       Applying it only at the end meant the answer streamed out to full height and then snapped
       shut — the customer watched a wall of text appear and vanish (owner, 2026-09-13). Clamping up
       front means it simply never grows past six lines.

       applyCollapse() at the end of the turn still has the final say: it removes this again if the
       finished answer turns out to fit and brought no cards with it. */
    if (collapsible && collapseApplies()) box.classList.add('llm-cah--collapsed');

    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '', answer = '', raw = '';
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      const text = dec.decode(chunk.value, { stream: true });
      raw += text;
      buf += text;
      const lines = buf.split('\n');
      buf = lines.pop();
      for (const line of lines) {
        if (!line.startsWith('data: ') || line.indexOf('[DONE]') > -1) continue;
        try {
          const parsed = JSON.parse(line.slice(6));
          const delta = parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content;
          /* ⛔ WAS `reply.textContent = answer`, which shipped the model's raw markdown to
             customers — "1. **McKesson … Size 4** is ideal for…", asterisks and all, on desktop and
             mobile alike (owner, 2026-09-13). The collection-page helper had formatted this all
             along; this renderer simply never did.

             LLM_ANSWER.format() ESCAPES FIRST and only then introduces its own handful of tags, so
             setting innerHTML here is safe — model output can never inject markup. If the shared
             script is missing, fall back to textContent: unformatted is ugly, unescaped would be a
             vulnerability. */
          if (delta) {
            answer += delta;
            if (window.LLM_ANSWER) reply.innerHTML = window.LLM_ANSWER.format(answer);
            else reply.textContent = answer;
          }
        } catch (e) { /* tool-call frame carrying the sources */ }
      }
    }
    zeroConvo.push({ role: 'assistant', content: answer });

    /* Link only what the reply NAMES — the same rule the collection helper uses for cards,
       so a link can never point at a product the answer never recommended. Keyed by handle:
       a Set of objects dedupes by reference, which listed every product twice. */
    const byHandle = new Map();
    const re = /"title":"([^"]{6,90})","handle":"([a-z0-9-]+)"/g;
    const unescaped = raw.replace(/\\"/g, '"');
    let m;
    while ((m = re.exec(unescaped)) !== null) {
      if (!byHandle.has(m[2])) byHandle.set(m[2], { title: m[1], handle: m[2] });
    }
    const named = [];
    byHandle.forEach(function (t) {
      const key = t.title.split(/\s+/).slice(0, 3).join(' ').toLowerCase();
      if (answer.toLowerCase().indexOf(key) > -1) named.push(t);
    });
    if (named.length) {
      const cards = document.createElement('div');
      cards.className = 'llm-cah__cards';
      cards.innerHTML = named.slice(0, 6).map(function (t) {
        return '<article class="llm-cah__card"><p class="llm-cah__name"><a href="/products/' +
          t.handle + '">' + t.title.replace(/[<>&"]/g, '') + '</a></p></article>';
      }).join('');
      body.appendChild(cards);
    }

    applyCollapse(box, reply, collapsible);
  }

  /* Collapse the WHOLE helper box on a phone — answer, product cards and the "Ask" form — behind a
     single "Show rest of answer".

     ⚠️ ONLY FOR AN ANSWER THE CUSTOMER DID NOT ASK FOR. `collapsible` is true only on the two
     automatic entry points, both of which are driven by whatever went into the TOP SEARCH BAR. If
     they typed into this box's own Ask field, the whole thing stays open — hiding the answer someone
     deliberately requested would be perverse (owner, 2026-09-13). The collection-page helper never
     collapses for the same reason.

     Collapsing only the paragraph was not enough: the cards and the Ask form still sat between the
     answer and the product grid, so the box kept most of a phone screen and the results the customer
     actually came for stayed below the fold.

     Triggered by overflow OR by the presence of cards — a three-line answer with five cards is
     still a tall box, so height, not word count, is what matters.

     A FOLLOW-UP CLEARS IT. Without the else-branch the class from the first answer would still be on
     the box and would hide the reply they just asked for. */
  /* Mobile only, and checked in JS as well as CSS. The CSS that does the collapsing lives inside a
     749px media query, so on desktop the class is inert — but `hasCards` below would still have been
     true, and a "Show rest of answer" button would have appeared on a desktop box with nothing
     hidden behind it. */
  function collapseApplies() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function applyCollapse(box, reply, collapsible) {
    const more = box.querySelector('.llm-cah__more');
    if (!collapsible || !collapseApplies()) {
      box.classList.remove('llm-cah--collapsed');
      if (more) more.remove();
      return;
    }
    box.classList.add('llm-cah--collapsed');
    /* Measured with the class already applied, so scrollHeight/clientHeight reflect the clamp.
       On desktop the collapse CSS sits inside the mobile media query, so nothing is clamped, the two
       are equal, there are no hidden cards to reveal, and no button is added. */
    const clipped = reply.scrollHeight > reply.clientHeight + 2;
    const hasCards = !!box.querySelector('.llm-cah__cards');
    if (!clipped && !hasCards) { box.classList.remove('llm-cah--collapsed'); return; }
    if (more) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'llm-cah__more';
    btn.textContent = 'Show rest of answer';
    /* One-way: someone who opened it is reading it, and collapsing it back under them mid-read
       would lose their place. */
    btn.addEventListener('click', function () {
      box.classList.remove('llm-cah--collapsed');
      btn.remove();
    });
    /* After the conversation, before the Ask form — so it reads as "there is more below this",
       and it is still visible while the form itself is hidden. */
    const convoEl = box.querySelector('.llm-cah__convo');
    if (convoEl) convoEl.insertAdjacentElement('afterend', btn);
    else box.appendChild(btn);
  }

  /* A QUESTION is not a product search. "will my insurance take care of the cost?" returned
     43 products - matchingStrategy "frequency" drops words until something matches, so "cost"
     and "care" pulled back unrelated stock and the customer got noise instead of an answer
     (owner 2026-08-20). Because that is not zero results, the help panel never appeared.
     So the panel is driven by the SHAPE of the query as well as by the hit count: anything
     phrased as a question gets answered, and any products found still render underneath. */
  const QUESTION_RE = /\?|^\s*(will|can|could|would|do|does|did|is|are|am|should|how|what|why|when|where|who|which)\b/i;
  function looksLikeQuestion(q) {
    return QUESTION_RE.test(q) || q.trim().split(/\s+/).length >= 6;
  }

  function renderQuestionHelp() {
    if (!searchTerm || !looksLikeQuestion(searchTerm)) return;
    const box = zeroHelpBox();
    box.hidden = false;
    if (zeroHelpStarted) return;
    zeroHelpStarted = true;
    box.querySelector('.llm-cah__title').textContent = 'Answering your question';
    zeroTurn(searchTerm, searchTerm, true);
  }

  function renderZeroResultHelp() {
    if (looksLikeQuestion(searchTerm)) return renderQuestionHelp();
    if (!searchTerm) return;
    const box = zeroHelpBox();
    box.hidden = false;
    if (zeroHelpStarted) return;
    zeroHelpStarted = true;
    zeroTurn(
      'I searched the shop for "' + searchTerm + '" and nothing came up. Do you carry it? ' +
      'If you do not, say so plainly and ask what I need it for. Only suggest something else ' +
      'if it is genuinely the same KIND of product, never a different category just to have ' +
      'something to offer.',
      'Searched for "' + searchTerm + '"',
      /* Collapsible: this answer was triggered by the top search bar, not asked for. */
      true
    );
  }

  function renderCount(total) {
    if (total === 0) {
      countEl.textContent = searchTerm ? `No results for "${searchTerm}"` : 'No results';
    } else {
      const start = currentOffset + 1;
      const end = Math.min(currentOffset + PER_PAGE, total);
      const forTerm = searchTerm ? ` for "${searchTerm}"` : '';
      countEl.textContent = `Showing ${start}-${end} of ${total} results${forTerm}`;
    }

    // Shopify's own native `search` object still runs in the
    // background even though we never render its results, and its
    // page-load-time <title> (built from search.results_count) can
    // differ from Meilisearch's real, product-only count - confirmed
    // directly (native said 290, Meilisearch said 223, for the same
    // query). Overwrite it with the true count once we know it.
    if (searchTerm) {
      document.title = document.title.replace(/^Search:\s*\d+\s*results? found/i, `Search: ${total} results found`);
    }
  }

  function buildPageParts(currentPage, totalPages) {
    const parts = [];
    const windowSize = 1;
    let lastAdded = 0;

    for (let page = 1; page <= totalPages; page += 1) {
      const inWindow = Math.abs(page - currentPage) <= windowSize;
      if (page === 1 || page === totalPages || inWindow) {
        if (lastAdded && page - lastAdded > 1) parts.push({ gap: true });
        parts.push({ page });
        lastAdded = page;
      }
    }
    return parts;
  }

  function goToPage(page) {
    currentOffset = (page - 1) * PER_PAGE;
    runSearch();
    root.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  function renderPagination(total) {
    const totalPages = Math.ceil(total / PER_PAGE);
    const currentPage = Math.floor(currentOffset / PER_PAGE) + 1;
    paginationEl.innerHTML = '';
    if (totalPages <= 1) return;

    const nav = document.createElement('nav');
    nav.setAttribute('aria-label', 'Pagination');

    const list = document.createElement('ul');
    list.className = 'pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto';

    const prevLi = document.createElement('li');
    prevLi.className = 'pagination__item pagination__item--arrow';
    const prevLink = document.createElement('a');
    prevLink.className = 'pagination__arrow pagination__arrow--prev hover:lighten flex text-current items-center has-ltr-icon';
    prevLink.href = '#';
    prevLink.setAttribute('role', 'link');
    if (currentPage <= 1) prevLink.setAttribute('aria-disabled', 'true');
    prevLink.innerHTML = '<span class="pagination__prev-label mis-2">Previous</span>';
    if (currentPage > 1) {
      prevLink.addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(currentPage - 1);
      });
    }
    prevLi.appendChild(prevLink);
    list.appendChild(prevLi);

    for (const part of buildPageParts(currentPage, totalPages)) {
      const li = document.createElement('li');
      // Show page numbers on ALL breakpoints (owner 2026-07-30) — was `hidden md:block` (mobile
      // showed only Previous/Next). Matches the collection pagination change.
      li.className = 'block';
      if (part.gap) {
        li.innerHTML = '<span class="block leading-none ml-2 mr-2">&hellip;</span>';
      } else if (part.page === currentPage) {
        li.innerHTML = `<span class="pagination__page-current font-bold block leading-none">${part.page}</span>`;
      } else {
        const link = document.createElement('a');
        link.className = 'pagination__page-link text-current block leading-none';
        link.href = '#';
        link.textContent = String(part.page);
        link.addEventListener('click', (e) => {
          e.preventDefault();
          goToPage(part.page);
        });
        li.appendChild(link);
      }
      list.appendChild(li);
    }

    const nextLi = document.createElement('li');
    nextLi.className = 'pagination__item pagination__item--arrow';
    const nextLink = document.createElement('a');
    nextLink.className = 'pagination__arrow pagination__arrow--next hover:lighten flex justify-end text-current items-center has-ltr-icon';
    nextLink.href = '#';
    nextLink.setAttribute('role', 'link');
    if (currentPage >= totalPages) nextLink.setAttribute('aria-disabled', 'true');
    nextLink.innerHTML = '<span class="pagination__next-label text-end">Next</span>';
    if (currentPage < totalPages) {
      nextLink.addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(currentPage + 1);
      });
    }
    nextLi.appendChild(nextLink);
    list.appendChild(nextLi);

    nav.appendChild(list);
    paginationEl.appendChild(nav);
  }

  function renderFacets(facetDistribution) {
    const openFields = new Set(
      [...filtersEl.querySelectorAll('details[open]')].map((d) => d.dataset.field)
    );
    filtersEl.innerHTML = '';

    // Merchant-controlled via Facet Field Config's Field Order (Content >
    // Metaobjects in Shopify admin) - lower numbers first, unranked
    // fields fall after the ranked ones in their natural order.
    const fieldOrder = (field) => FIELD_CONFIG[field]?.fieldOrder ?? null;
    const fields = Object.keys(facetDistribution)
      .filter((f) => !isExcludedField(f))
      .sort((a, b) => {
        const orderA = fieldOrder(a);
        const orderB = fieldOrder(b);
        if (orderA != null && orderB != null) return orderA - orderB;
        if (orderA != null) return -1;
        if (orderB != null) return 1;
        return 0;
      });
    if (fields.length === 0) return;

    const form = document.createElement('form');
    form.className = 'llm-meili-facets';
    form.addEventListener('submit', (e) => e.preventDefault());

    for (const field of fields) {
      const values = facetDistribution[field];
      const valueEntries = Object.entries(values).filter(([, count]) => count > 0);
      if (valueEntries.length === 0) continue;
      const groupedValues = groupFacetValues(field, valueEntries);

      const details = document.createElement('details');
      details.className = 'llm-meili-facet disclosure';
      details.dataset.field = field;
      if (openFields.has(field) || config.expandFilters) details.open = true;

      const summary = document.createElement('summary');
      summary.textContent = fieldLabel(field);
      details.appendChild(summary);

      const list = document.createElement('div');
      list.className = 'llm-meili-facet__options';

      groupedValues.forEach((group, index) => {
        const activeSet = activeFilters.get(field);
        const isChecked = activeSet ? group.rawValues.some((v) => activeSet.has(v)) : false;
        const row = document.createElement('label');
        row.className = 'llm-meili-facet__option';
        if (index >= MAX_VISIBLE_OPTIONS) row.hidden = true;

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = isChecked;
        checkbox.addEventListener('change', () => {
          toggleFilterGroup(field, group.rawValues, checkbox.checked);
        });

        const labelWrap = document.createElement('span');
        labelWrap.className = 'llm-meili-facet__option-label';
        const text = document.createElement('span');
        text.textContent = group.label;
        labelWrap.append(checkbox, text);
        row.appendChild(labelWrap);

        if (config.showFilterCounts) {
          const countEl2 = document.createElement('span');
          countEl2.className = 'llm-meili-facet__option-count';
          countEl2.textContent = `(${group.count})`;
          row.appendChild(countEl2);

          if (group.rawValues.length > 1) {
            fetchAccurateGroupCount(field, group.rawValues).then((accurate) => {
              if (accurate != null) countEl2.textContent = `(${accurate})`;
            });
          }
        }

        list.appendChild(row);
      });

      details.appendChild(list);

      if (groupedValues.length > MAX_VISIBLE_OPTIONS) {
        const showMore = document.createElement('button');
        showMore.type = 'button';
        showMore.className = 'llm-meili-facet__show-more link';
        showMore.textContent = 'Show more';
        showMore.addEventListener('click', () => {
          list.querySelectorAll('.llm-meili-facet__option[hidden]').forEach((el) => {
            el.hidden = false;
          });
          showMore.remove();
        });
        details.appendChild(showMore);
      }

      form.appendChild(details);
    }

    if (activeFilters.size > 0) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'llm-meili-clear-all link';
      clearBtn.textContent = 'Clear all filters';
      clearBtn.addEventListener('click', () => {
        activeFilters.clear();
        currentOffset = 0;
        runSearch();
      });
      form.appendChild(clearBtn);
    }

    filtersEl.appendChild(form);
  }

  // rawValues is every raw index value merged into the checked/unchecked
  // group (usually just one, more when the merchant has mapped several
  // raw values to the same display label) - all added/removed together.
  function toggleFilterGroup(field, rawValues, isChecked) {
    if (!activeFilters.has(field)) activeFilters.set(field, new Set());
    const set = activeFilters.get(field);
    for (const value of rawValues) {
      if (isChecked) set.add(value);
      else set.delete(value);
    }
    if (set.size === 0) activeFilters.delete(field);

    currentOffset = 0;
    runSearch();
  }

  async function renderGrid(hits) {
    if (hits.length === 0) {
      /* The count line directly above already says 'No results for "rearz"'. Repeating it as
         "No products found." said the same thing twice and offered nothing either time — a
         dead end on the one page where the customer most needs help (owner 2026-08-20).
         Instead: hand the query to the assistant, which searches the whole catalogue and can
         say plainly whether we carry it and what comes closest. */
      gridEl.innerHTML = '';
      gridEl.removeAttribute('aria-busy');
      /* Mark ready even with nothing to show. The loading skeletons in main-search.liquid are
         gated on :not([data-ms-ready]); without this the grid keeps the skeleton grid rules on a
         zero-result page, and if the innerHTML wipe were ever removed the shimmer would sit there
         forever under the "no results" help. */
      gridEl.setAttribute('data-ms-ready', '');
      renderZeroResultHelp();
      return;
    }
    /* Keep the panel when the query is a question — the products below may be noise. */
    if (looksLikeQuestion(searchTerm)) renderQuestionHelp(); else hideZeroResultHelp();

    // No collection to scope the URL through here - a plain product
    // URL still resolves `product` for the Section Rendering API fetch.
    const cardHtmls = await Promise.all(
      hits.map((hit) =>
        fetch(`/products/${hit.handle}?section_id=api-product-card`)
          .then((res) => (res.ok ? res.text() : ''))
          .catch(() => '')
      )
    );

    /* Drop hits whose card came back as the ONBOARDING PLACEHOLDER.
       A product that has been deleted or unpublished can still sit in the Meilisearch index, and
       the Section Rendering API answers 200 for it — Shopify renders the section with `product`
       unset, and product-card.liquid's onboarding branch paints a fake card: "Product title", the
       default lamp image, $12.34 (its hardcoded 1234 placeholder price). Because the response IS
       ok, the empty-string guard above never fires, and that fake product shipped to customers
       alongside real ones, with an Add to cart button on it. Found by the owner searching
       "osotomy barrier" (2026-09-05).

       A real card always carries data-product-id on its <product-card>; the placeholder has no
       product, so no id and no links at all. That is the tell.

       This SHORTENS the grid rather than back-filling — the count line still comes from
       Meilisearch, so a page can show 15 of a claimed 16. That is the honest failure mode: a short
       page beats a fake product. The stale INDEX ENTRY is the real defect and has to be fixed in
       the sync worker; this only stops it reaching a customer. */
    gridEl.innerHTML = cardHtmls
      .filter((html) => html && html.indexOf('data-product-id') !== -1)
      .map((html) => `<li class="js-pagination-result">${html}</li>`)
      .join('');
    gridEl.removeAttribute('aria-busy');
    /* First real paint — clears the loading skeletons (main-search.liquid gates them on
       :not([data-ms-ready])). Set AFTER innerHTML, so the shimmer stays right up until real cards
       replace it rather than blanking early. */
    gridEl.setAttribute('data-ms-ready', '');
  }

  sortEl.addEventListener('change', () => {
    currentSort = sortEl.value;
    currentOffset = 0;
    runSearch();
  });

  runSearch();
})();
